import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileArrowUp, FilePdf, FileDoc, FileImage, FileCsv, FileCode,
  FileText, FileArrowDown, Trash, MagnifyingGlass, Eye, Tag, X, Check,
  Package, Archive,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const KIND_META = {
  sop:       { label: "SOP",        color: "#00E5FF" },
  image:     { label: "Image",      color: "#FFB000" },
  worksheet: { label: "Worksheet",  color: "#00E676" },
  workflow:  { label: "Workflow",   color: "#8B5CF6" },
  event_log: { label: "Event Log",  color: "#FF3B30" },
  document:  { label: "Document",   color: "#9CA3AF" },
};

const kindIcon = (kind, ext) => {
  const size = 22;
  const map = {
    pdf: <FilePdf size={size} weight="fill" />, docx: <FileDoc size={size} weight="fill" />,
    doc: <FileDoc size={size} weight="fill" />, png: <FileImage size={size} weight="fill" />,
    jpg: <FileImage size={size} weight="fill" />, jpeg: <FileImage size={size} weight="fill" />,
    csv: <FileCsv size={size} weight="fill" />, xlsx: <FileCsv size={size} weight="fill" />,
    xes: <FileCode size={size} weight="fill" />, json: <FileCode size={size} weight="fill" />,
    xml: <FileCode size={size} weight="fill" />, log: <FileText size={size} weight="fill" />,
    txt: <FileText size={size} weight="fill" />, md: <FileText size={size} weight="fill" />,
  };
  return map[ext] || <FileText size={size} weight="fill" />;
};

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MediaLibrary() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [tagsUniverse, setTagsUniverse] = useState([]);
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [uploadTags, setUploadTags] = useState("");
  const [editingTags, setEditingTags] = useState(null); // file id
  const [tagsDraft, setTagsDraft] = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: f }, { data: t }] = await Promise.all([
        api.get("/files"), api.get("/files/tags")
      ]);
      setFiles(f);
      setTagsUniverse(t);
    } catch (_e) { toast.error("Failed to load files"); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = uploadTags.trim() ? `/files/upload?tags=${encodeURIComponent(uploadTags.trim())}` : "/files/upload";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.chunks_indexed
        ? `${file.name} · ${data.chunks_indexed} chunks indexed`
        : `${file.name} uploaded`);
      setUploadTags("");
      load();
    } catch (_e) { toast.error("Upload failed"); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.original_filename}? RAG chunks will also be removed.`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const saveTags = async (f) => {
    try {
      const list = tagsDraft.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
      const { data } = await api.patch(`/files/${f.id}/tags`, { tags: list });
      toast.success(`Tags updated (${data.tags.length})`);
      setEditingTags(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update tags"); }
  };

  const startEditTags = (f) => {
    setEditingTags(f.id);
    setTagsDraft((f.tags || []).join(", "));
  };

  const filtered = useMemo(() => {
    return files.filter((f) => {
      if (kindFilter !== "all" && f.kind !== kindFilter) return false;
      if (tagFilter !== "all" && !(f.tags || []).includes(tagFilter)) return false;
      if (query) {
        const q = query.toLowerCase();
        return f.original_filename.toLowerCase().includes(q) ||
               (f.kind || "").toLowerCase().includes(q) ||
               (f.extension || "").toLowerCase().includes(q) ||
               (f.tags || []).some((t) => t.includes(q));
      }
      return true;
    });
  }, [files, query, kindFilter, tagFilter]);

  const stats = useMemo(() => {
    const by = {};
    let total = 0;
    files.forEach((f) => {
      by[f.kind] = (by[f.kind] || 0) + 1;
      total += f.size || 0;
    });
    return { by, total };
  }, [files]);

  const toggleSelect = (id) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelection(next);
  };
  const selectAll = () => setSelection(new Set(filtered.map((f) => f.id)));
  const clearSelection = () => setSelection(new Set());

  const canBulkDelete = useMemo(() => {
    if (!selection.size) return false;
    if (user?.role === "admin") return true;
    return filtered.filter((f) => selection.has(f.id)).every((f) => f.user_id === user?.id);
  }, [selection, filtered, user]);

  const bulkDownload = async () => {
    try {
      const res = await fetch(`${API}/files/bulk-download`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("itsme_token") || ""}`,
        },
        body: JSON.stringify({ ids: Array.from(selection) }),
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `itsme-files-${Date.now()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selection.size} file${selection.size > 1 ? "s" : ""} as zip`);
    } catch (_e) { toast.error("Bulk download failed"); }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selection.size} file${selection.size > 1 ? "s" : ""}? This can't be undone from the UI.`)) return;
    try {
      const { data } = await api.post("/files/bulk-delete", { ids: Array.from(selection) });
      toast.success(`Deleted ${data.deleted} of ${data.requested}`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
  };

  const downloadUrl = (f) => `${API}/files/${f.id}/download`;
  const isImage = (f) => ["png", "jpg", "jpeg", "webp", "gif"].includes(f.extension);

  const doDownload = async (f) => {
    try {
      const res = await fetch(`${API}/files/${f.id}/download`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("itsme_token") || ""}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="data-label">Library</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Media & Files</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Every SOP, event log, image, and worksheet ingested into ITSME — powered by S3-compatible object storage.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Input
            data-testid="upload-tags-input"
            placeholder="Tags for next upload (comma-separated)"
            value={uploadTags}
            onChange={(e) => setUploadTags(e.target.value)}
            className="bg-[#0F1117] border-[#272A35] w-64"
          />
          <label className="cursor-pointer" data-testid="upload-btn">
            <div className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044cc] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
              <FileArrowUp size={16} /> {uploading ? "Uploading…" : "Upload file"}
            </div>
            <input ref={fileInputRef} type="file" hidden onChange={onUpload}
                   accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.xes,.log,.lst,.png,.jpg,.jpeg,.json,.xml,.txt,.md" />
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <StatCard label="Total files" value={files.length} accent="#F3F4F6" />
        <StatCard label="Total size" value={fmtBytes(stats.total)} accent="#00E5FF" />
        {Object.entries(KIND_META).map(([k, meta]) => (
          <StatCard key={k} label={meta.label} value={stats.by[k] || 0} accent={meta.color} />
        ))}
      </div>

      {/* Filters + tag chips */}
      <div className="itsme-card p-3 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="media-search"
                   placeholder="Search by name, kind, extension, or tag"
                   value={query} onChange={(e) => setQuery(e.target.value)}
                   className="pl-8 bg-[#0F1117] border-[#272A35]" />
          </div>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger data-testid="media-kind-filter" className="sm:w-44 bg-[#0F1117] border-[#272A35]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#13151A] border-[#272A35] text-slate-100">
              <SelectItem value="all">All kinds</SelectItem>
              {Object.entries(KIND_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger data-testid="media-tag-filter" className="sm:w-44 bg-[#0F1117] border-[#272A35]">
              <SelectValue placeholder="All tags" />
            </SelectTrigger>
            <SelectContent className="bg-[#13151A] border-[#272A35] text-slate-100">
              <SelectItem value="all">All tags</SelectItem>
              {tagsUniverse.map((t) => (
                <SelectItem key={t.tag} value={t.tag}>{t.tag} · {t.count}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {tagsUniverse.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="tag-cloud">
            {tagsUniverse.slice(0, 20).map((t) => (
              <button
                key={t.tag}
                onClick={() => setTagFilter(tagFilter === t.tag ? "all" : t.tag)}
                data-testid={`tag-chip-${t.tag}`}
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
                  tagFilter === t.tag
                    ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                    : "border-[#272A35] text-slate-400 hover:border-[#00E5FF]/50 hover:text-slate-200"
                }`}
              >
                <Tag size={10} /> {t.tag}
                <span className="text-slate-500 font-mono">{t.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selection.size > 0 && (
        <div className="itsme-card p-3 flex items-center gap-3 border-[#00E5FF]/40"
             data-testid="bulk-bar">
          <div className="text-sm text-slate-200 font-medium">
            {selection.size} selected
          </div>
          <Button size="sm" variant="outline" onClick={clearSelection}
                  className="border-[#272A35] text-slate-300">
            Clear
          </Button>
          <div className="flex-1" />
          <Button size="sm" data-testid="bulk-download-btn"
                  onClick={bulkDownload}
                  className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 hover:bg-[#00E5FF]/20">
            <Archive size={14} className="mr-1" /> Download as zip
          </Button>
          {canBulkDelete && (
            <Button size="sm" data-testid="bulk-delete-btn"
                    onClick={bulkDelete}
                    className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20">
              <Trash size={14} className="mr-1" /> Delete all
            </Button>
          )}
        </div>
      )}

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="itsme-card p-16 text-center">
          <FileText size={40} className="mx-auto text-slate-600" />
          <div className="text-slate-400 text-sm mt-3">
            {files.length === 0 ? "No files uploaded yet. Drop your first SOP or event log to get started." : "No files match this filter."}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
            <div>{filtered.length} file{filtered.length !== 1 && "s"}</div>
            <button onClick={selectAll} data-testid="select-all"
                    className="hover:text-slate-200 flex items-center gap-1">
              <Package size={12} /> Select all filtered
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((f) => {
              const meta = KIND_META[f.kind] || KIND_META.document;
              const canDelete = user?.role === "admin" || f.user_id === user?.id;
              const isSelected = selection.has(f.id);
              return (
                <div key={f.id} data-testid={`media-${f.id}`}
                     className={`itsme-card p-4 flex flex-col transition-colors ${
                       isSelected ? "border-[#00E5FF] bg-[#00E5FF]/[0.03]" : "itsme-card-hover"
                     }`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleSelect(f.id)}
                            data-testid={`select-${f.id}`}
                            className={`w-5 h-5 rounded border shrink-0 grid place-items-center transition-colors ${
                              isSelected ? "bg-[#00E5FF] border-[#00E5FF]" : "border-[#272A35] hover:border-slate-500"
                            }`}>
                      {isSelected && <Check size={12} weight="bold" color="#0B0D12" />}
                    </button>
                    <div className="w-10 h-10 rounded-md grid place-items-center shrink-0"
                         style={{ background: `${meta.color}18`, color: meta.color }}>
                      {kindIcon(f.kind, f.extension)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" title={f.original_filename}>{f.original_filename}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border"
                              style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}10` }}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">.{f.extension}</span>
                        <span className="text-[10px] text-slate-500">·</span>
                        <span className="text-[10px] text-slate-500">{fmtBytes(f.size)}</span>
                      </div>
                      {!!f.chunks_indexed && (
                        <div className="text-[10px] mt-1.5 text-[#00E5FF]">
                          {f.chunks_indexed} RAG chunks indexed
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tags row */}
                  <div className="mt-3">
                    {editingTags === f.id ? (
                      <div className="flex gap-2">
                        <Input data-testid={`tags-edit-${f.id}`}
                               value={tagsDraft} onChange={(e) => setTagsDraft(e.target.value)}
                               placeholder="tag1, tag2"
                               className="h-8 text-xs bg-[#0F1117] border-[#272A35]"
                               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveTags(f))} />
                        <Button size="sm" data-testid={`tags-save-${f.id}`}
                                onClick={() => saveTags(f)}
                                className="h-8 bg-[#0055FF] hover:bg-[#0044cc]">Save</Button>
                        <Button size="sm" variant="outline"
                                onClick={() => setEditingTags(null)}
                                className="h-8 border-[#272A35] text-slate-400 px-2"><X size={12} /></Button>
                      </div>
                    ) : (
                      <div className="flex items-center flex-wrap gap-1"
                           data-testid={`tags-${f.id}`}>
                        {(f.tags || []).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#272A35] text-slate-300 bg-[#13151A]">
                            <Tag size={9} className="inline mr-1" /> {t}
                          </span>
                        ))}
                        {canDelete && (
                          <button onClick={() => startEditTags(f)}
                                  data-testid={`edit-tags-${f.id}`}
                                  className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#00E5FF] px-1.5 py-0.5">
                            {(f.tags || []).length === 0 ? "+ Add tags" : "Edit"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {isImage(f) && (
                    <div className="mt-3 rounded-md overflow-hidden bg-[#0A0B0F] border border-[#1f2330] aspect-video grid place-items-center">
                      <img src={downloadUrl(f)} alt={f.original_filename}
                           className="max-h-full max-w-full object-contain cursor-pointer"
                           onClick={() => setPreviewFile(f)} />
                    </div>
                  )}

                  <div className="mt-auto pt-3 flex items-center gap-2">
                    <div className="text-[10px] text-slate-500 flex-1">
                      {new Date(f.created_at).toLocaleDateString()}
                    </div>
                    {isImage(f) && (
                      <button data-testid={`preview-${f.id}`}
                              onClick={() => setPreviewFile(f)}
                              className="p-1.5 rounded hover:bg-[#1C1F26] text-slate-400 hover:text-[#00E5FF]"
                              title="Preview">
                        <Eye size={14} />
                      </button>
                    )}
                    <button data-testid={`download-${f.id}`}
                            onClick={() => doDownload(f)}
                            className="p-1.5 rounded hover:bg-[#1C1F26] text-slate-400 hover:text-[#00E5FF]"
                            title="Download">
                      <FileArrowDown size={14} />
                    </button>
                    {canDelete && (
                      <button data-testid={`delete-${f.id}`}
                              onClick={() => remove(f)}
                              className="p-1.5 rounded hover:bg-[#1C1F26] text-slate-400 hover:text-[#FF3B30]"
                              title="Delete">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
             onClick={() => setPreviewFile(null)}
             data-testid="media-preview-modal">
          <div className="max-w-5xl max-h-[90vh] w-full flex flex-col items-center gap-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="w-full flex items-center justify-between">
              <div>
                <div className="font-heading font-semibold">{previewFile.original_filename}</div>
                <div className="text-xs text-slate-400">{fmtBytes(previewFile.size)} · {previewFile.extension}</div>
              </div>
              <button onClick={() => setPreviewFile(null)}
                      className="text-slate-300 hover:text-white text-sm"
                      data-testid="media-preview-close">Close ✕</button>
            </div>
            <img src={downloadUrl(previewFile)} alt={previewFile.original_filename}
                 className="max-h-[80vh] max-w-full rounded-md border border-[#272A35]" />
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="itsme-card p-3">
    <div className="data-label" style={{ color: "#9CA3AF" }}>{label}</div>
    <div className="font-heading text-lg font-bold mt-1" style={{ color: accent }}>{value}</div>
  </div>
);
